import { columnDisplayName } from '../../../../../ui/features/badge-management/components/BadgeAccountsSelectionPanel/types';
import { useBadgingTranslation } from '../../../../../ui/hooks';
import { useDataTablePagination } from '../../../../../ui/hooks/useDataTablePagination';
import { DataTable, TextCell } from '@juvare/common-data-table-ui/DataTable';
import { FiltersBar, useFilterConfig } from '@juvare/common-data-table-ui/FiltersBar';
import { EmptyContentWrapper } from '@juvare/common-web-ui/EmptyContentWrapper';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box, Button, IconButton, useMediaQuery } from '@mui/material';
import React, { useState } from 'react';
import { AccountOrganizationSelect } from './AccountOrganizationSelect';
export const AccountsSelectedTable = (props) => {
    const { t } = useBadgingTranslation();
    const isScreenStrictlyLarge = useMediaQuery((theme) => theme.breakpoints.between('lg', 'xl'));
    const [sortData, setSortData] = useState({
        sortColumnKey: columnDisplayName,
        sortColumnDesc: false,
    });
    const [filterData, setFilterData] = useState({});
    const filterConfig = useFilterConfig((builder) => {
        builder.quickSearchFilter({
            dataPath: columnDisplayName,
            label: t('badges.create.wizard.step3.accountSelection.tableFilterSearch'),
            sx: { maxWidth: 343 },
        });
    }, [t]);
    const accounts = sortAndFilterAccounts(props.selectedAccounts, sortData, filterData);
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const handleSelectedRowChanged = (row, selected) => {
        setSelectedRowIds((selectedIds) => getRowsSelectedAfterSelectionChanged(selectedIds, row, selected));
    };
    const handleSelectAllRowsChanged = (rows, selected) => {
        setSelectedRowIds((selectedIds) => rows.reduce((selectedIdsNew, row) => getRowsSelectedAfterSelectionChanged(selectedIdsNew, row, selected), selectedIds));
    };
    const handleAddAccounts = () => {
        var _a;
        setSelectedRowIds([]);
        (_a = props.onAddAccountClick) === null || _a === void 0 ? void 0 : _a.call(props);
    };
    const handleRemoveSelectedAccounts = () => {
        props.onUnselectAccounts(selectedRowIds);
        setSelectedRowIds([]);
    };
    const handleRemoveSelectedAccount = (id) => {
        props.onUnselectAccounts([id]);
        setSelectedRowIds([]);
    };
    const columnsDefault = [
        {
            headerLabel: t('badges.create.wizard.step3.accountSelection.tableColumnName'),
            cellFunction: (row) => React.createElement(TextCell, null, row.account.displayName),
            sortKey: columnDisplayName,
        },
        {
            headerLabel: t('badges.create.wizard.step3.accountSelection.tableColumnOrganization'),
            cellFunction: (row) => (React.createElement(AccountOrganizationSelect, { accountSelection: row, onOptionsPartialChange: (optionsChange) => {
                    props.onSetSelectedAccountOptions(row.id, optionsChange);
                }, hideDropdownWhenNoOptions: true, hideDropdownWhenOneOption: true })),
        },
        {
            cellFunction: (row) => {
                return (React.createElement(Box, { sx: {
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    } },
                    React.createElement(IconButton, { onClick: () => {
                            handleRemoveSelectedAccount(row.id);
                        }, "aria-label": 'Remove account' },
                        React.createElement(RemoveCircleOutlineIcon, { color: 'primary' }))));
            },
            tableCellSx: {
                width: 52,
                padding: 0,
            },
        },
    ];
    const columns = props.renderColumns ? props.renderColumns(columnsDefault) : columnsDefault;
    const pagination = useDataTablePagination({
        rows: accounts,
        totalRowCount: props.selectedAccounts.length,
        defaultRowsPerPage: 5,
    });
    return (React.createElement(Box, { sx: { width: '100%' } }, props.selectedAccounts.length > 0 ? (React.createElement(DataTable, Object.assign({ tableSx: {
            tableLayout: 'fixed',
        }, loading: false, columns: columns, filtersBar: React.createElement(FiltersBar, { config: filterConfig, onFilter: (data) => {
                setFilterData(data);
                pagination.onPageChange(0);
            } }), globalActions: [
            ...(props.onAddAccountClick
                ? [
                    {
                        icon: React.createElement(AddIcon, null),
                        label: t('badges.create.wizard.step3.accountSelection.addAccountButton'),
                        onClick: handleAddAccounts,
                    },
                ]
                : []),
            ...(selectedRowIds.length > 0
                ? [
                    {
                        icon: React.createElement(RemoveIcon, null),
                        label: t('badges.create.wizard.step3.accountSelection.removeAccountsButton'),
                        onClick: handleRemoveSelectedAccounts,
                    },
                ]
                : []),
        ], title: props.tableTitle, sortColumnKey: sortData === null || sortData === void 0 ? void 0 : sortData.sortColumnKey, sortColumnDesc: sortData === null || sortData === void 0 ? void 0 : sortData.sortColumnDesc, onSortColumnChange: (sortColumnKey, sortColumnDesc) => {
            setSortData({ sortColumnKey, sortColumnDesc });
        } }, pagination, { filteredRowCount: accounts.length, rowSelectionEnabled: props.tableRowSelectionEnabled, selectedRowIds: selectedRowIds, onRowSelectionChange: handleSelectedRowChanged, onSelectAllRowsInCurrentPageClick: handleSelectAllRowsChanged }))) : (React.createElement(EmptyContentWrapper, { description: 'No Accounts', imageSize: isScreenStrictlyLarge ? 'small' : 'medium' }, props.onAddAccountClick && (React.createElement(Button, { startIcon: React.createElement(AddIcon, null), variant: 'contained', onClick: props.onAddAccountClick }, t('badges.create.wizard.step3.accountSelection.addAccountButton')))))));
};
function sortAndFilterAccounts(selectedAccounts, sortData, filterData) {
    let accounts = selectedAccounts;
    if (sortData) {
        accounts.sort(sortAccounts(sortData));
    }
    const filterDisplayName = filterData.displayName;
    if (filterDisplayName) {
        accounts = accounts.filter((row) => row.account.displayName.toLowerCase().includes(filterDisplayName.toLowerCase()));
    }
    return accounts;
}
function sortAccounts(sortOptions) {
    const { sortColumnKey, sortColumnDesc } = sortOptions;
    return (a1, a2) => {
        if (sortColumnKey === columnDisplayName) {
            return a1.account.displayName.localeCompare(a2.account.displayName, [], { sensitivity: 'base' }) * (sortColumnDesc ? -1 : 1);
        }
        return -1;
    };
}
function getRowsSelectedAfterSelectionChanged(selectedIds, row, selected) {
    const selectedIdsWithoutCurrent = selectedIds.filter((r) => r !== row.id);
    return selected ? [...selectedIdsWithoutCurrent, row.id] : selectedIdsWithoutCurrent;
}
